import React, { useEffect, useState } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import { getContestClasses, getContestClass, getContestScorecard, setContestClass, setContestTick } from '../../utils/api';
import { useLocalStorage } from '../../utils/use-local-storage';
import Loading from '../loading';
import { yellow, red, green, deepOrange, blue, brown, grey, pink } from '@mui/material/colors';
import Alert from '@mui/material/Alert';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Container from '@mui/material/Container';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import ListSubheader from '@mui/material/ListSubheader';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';

// 1. Define Interfaces
interface ContestClass {
  id: number;
  clazz: string;
}

interface Tick {
  top: boolean;
  name: string;
}

interface ScorecardEntry {
  id: number;
  nr: number;
  color: string;
  description: string;
  tickZone: boolean;
  tickTop: boolean;
  ticks: Tick[];
}

interface ErrorMessage {
  title: string;
  message: string;
}

const getColorSx = (colorName) => {
  switch(colorName) {
    case 'yellow': return { color: '#000000', bgcolor: yellow[500] };
    case 'red': return { bgcolor: red[500] };
    case 'green': return { bgcolor: green[500] };
    case 'orange': return { bgcolor: deepOrange[500] };
    case 'blue': return { bgcolor: blue[500] };
    case 'brown': return { bgcolor: brown[500] };
    case 'grey': return { bgcolor: grey[500] };
    case 'pink': return { bgcolor: pink[500] };
    case 'white': return { color: '#000000', bgcolor: '#FFFFFF' };
    case 'black': return { color: '#FFFFFF', bgcolor: '#000000' };
    default: return {};
  }
};

enum Hide {
  None = 0,
  Zone = 1,
  Top = 2,
}
enum Sort {
  Number = 0,
  Description = 1,
  Easiest = 2,
  Hardest = 3,
}

const Scorecard = () => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<ErrorMessage | null>(null);
  const [clazzes, setClazzes] = useState<ContestClass[]>([]);
  const [ticks, setTicks] = useState<Tick[] | null>(null);
  const [clazzId, setClazzId] = useState<number | null>(null);
  const [scorecard, setScorecard] = useState<ScorecardEntry[]>([]);
  const [hide, setHide] = useLocalStorage("hide", Hide.None);
  const [sort, setSort] = useLocalStorage("sort", Sort.Number);
  
  useEffect(() => {
    if (isAuthenticated) {
      getAccessTokenSilently().then((token) => setAccessToken(token));
    }
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    if (!isLoading && accessToken) {
      getContestClass(accessToken).then((data) => {
        if (data) {
          setClazzId(data.id);
        }
      })
      getContestClasses().then((data) => {
        setClazzes(data);
      })
    }
  }, [isLoading, accessToken]);

  useEffect(() => {
    if (!isLoading && accessToken && clazzId) {
      getContestScorecard(accessToken, clazzId).then((data) => {
        setScorecard(data
          .sort((a: ScorecardEntry, b: ScorecardEntry) => {
            let res = 0;
            if (sort === Sort.Description) {
              res = a.description.localeCompare(b.description);
            }
            if (sort === Sort.Easiest) {
              res = (b.ticks?.filter(t => t.top).length || 0) - (a.ticks?.filter(t => t.top).length || 0);
              if (res === 0) {
                res = (b.ticks?.length || 0) - (a.ticks?.length || 0);
              }
            } else if (sort === Sort.Hardest) {
              res = (a.ticks?.filter(t => t.top).length || 0) - (b.ticks?.filter(t => t.top).length || 0);
              if (res === 0) {
                res = (a.ticks?.length || 0) - (b.ticks?.length || 0);
              }
              if (res === 0) {
                res = a.nr - b.nr;
              }
            }
            if (res === 0) {
              res = a.nr - b.nr;
            }
            return res;
          })
          .filter((x: ScorecardEntry) => {
            if (hide === Hide.Zone && (x.tickZone || x.tickTop)) {
              return false;
            }
            else if (hide === Hide.Top && x.tickTop) {
              return false;
            }
            return true;
          })
        );
      });
    }
  }, [isLoading, accessToken, clazzId, hide, sort]);
  
  if (isLoading || (isAuthenticated && clazzes.length===0) || (isAuthenticated && clazzId && scorecard.length===0)) {
    return <Loading/>
  }
  else if (!isAuthenticated) {
    return <Alert severity="info">Click on <b>SIGN IN</b> above to get your scorecard.</Alert>
  }
  
  let content: React.ReactNode = null;
  if (!clazzId) {
    content = <Alert severity="info">Choose your class ({clazzes.map(clazz => clazz.clazz).join("/")}) to get your scorecard.</Alert>
  }
  else {
    content = (
      <List dense>
        {scorecard.map(r => {
          const avatarSx = getColorSx(r.color);

          let secondary = "0 top (1000p), 0 zone (500p)";
          if (r.ticks && r.ticks.length>0) {
            const numTicks = r.ticks.length;
            const numTickTop = r.ticks.filter(t => t.top).length;
            const numTickZone = r.ticks.filter(t => !t.top).length;
            const pointsForTop = 500 / (numTickTop == 0 ? 1 : numTickTop);
            const pointsForZone = 500 / (numTicks == 0 ? 1 : numTicks);
            secondary = `${numTickTop} top (${Math.round(pointsForTop+pointsForZone)}p), ${numTickZone} zone (${Math.round(pointsForZone)}p)`
          }
          
          return (
            <ListItem key={r.id}>
              <ListItemAvatar>
                <Avatar sx={avatarSx}>{r.nr}</Avatar>
              </ListItemAvatar>
              <ListItemText 
                primary={r.description} 
                secondary={secondary} 
                onClick={() => setTicks(r.ticks && r.ticks.length > 0 ? r.ticks : null)}
                sx={{ cursor: 'pointer' }}
              />
              <ToggleButtonGroup color="primary" size="small">
                <ToggleButton value="check" selected={r.tickZone} onChange={() => {
                    setContestTick(accessToken, clazzId, r.id, !r.tickZone, false).then((problem) => {
                      let newScorecard = scorecard.map(x => x.id===r.id? problem : x);
                      setScorecard([...newScorecard]);
                    });
                  }}>
                  Zone
                </ToggleButton>
                <ToggleButton value="check" selected={r.tickTop} onChange={() => {
                    setContestTick(accessToken, clazzId, r.id, (!r.tickTop ? true : r.tickZone), !r.tickTop).then((problem) => {
                      let newScorecard = scorecard.map(x => x.id===r.id? problem : x);
                      setScorecard([...newScorecard]);
                    });
                  }}>
                  Top
                </ToggleButton>
              </ToggleButtonGroup>
            </ListItem>
          )
        })}
      </List>
    )
  }
  return (
    <>
      <Dialog
        open={errorMessage != null}
        onClose={() => setErrorMessage(null)}>
        <DialogTitle>{errorMessage?.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {errorMessage?.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorMessage(null)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ticks != null} onClose={() => setTicks(null)}>
        <DialogContent>
        <List dense={true}>
          {ticks?.filter(t => t.top).length! > 0 && (
            <>
              <ListSubheader disableSticky>
                Top:
              </ListSubheader>
              {ticks?.filter(t => t.top).map((t, i) => <ListItem key={i}><ListItemText primary={t.name}/></ListItem>)}
            </>
          )}
          {ticks?.filter(t => !t.top).length! > 0 && (
            <>
              <ListSubheader disableSticky>
                Zone:
              </ListSubheader>
              {ticks?.filter(t => !t.top).map((t, i) => <ListItem key={i}><ListItemText primary={t.name}/></ListItem>)}
            </>
          )}
        </List>
        </DialogContent>
      </Dialog>

      <Container sx={{ display: "flex", justifyContent: "center", gap: 1, padding: 2, flexDirection: 'column' }}>
        <FormControl size="small" fullWidth>
          <InputLabel>Class</InputLabel>
          <Select value={clazzId || ''} label="Class" onChange={(event: SelectChangeEvent<number>) => {
            const newClazzId = typeof event.target.value === 'string' ? parseInt(event.target.value) : event.target.value;
            if (clazzId && clazzId > 0) {
              getContestScorecard(accessToken, clazzId).then((data) => {
                if (data.filter(x => x.tickZone).length > 0) {
                  setErrorMessage({title: "Change class", message: "You have already logged boulders in your class. You have to un-tick these to be able to change class."});
                }
                else {
                  setContestClass(accessToken, newClazzId).then(() => setClazzId(newClazzId));
                }
              });
            }
            else {
              setContestClass(accessToken, newClazzId).then(() => setClazzId(newClazzId));
            }
          }}>
            {clazzes.map(({id, clazz}) => <MenuItem key={id} value={id}>{clazz}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Hide</InputLabel>
          <Select value={hide} label="Hide" onChange={(event: SelectChangeEvent) => setHide(event.target.value)}>
            <MenuItem value={Hide.None}>None</MenuItem>
            <MenuItem value={Hide.Zone}>Zone</MenuItem>
            <MenuItem value={Hide.Top}>Top</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" fullWidth>
          <InputLabel>Sort</InputLabel>
          <Select value={sort} label="Sort" onChange={(event: SelectChangeEvent) => setSort(event.target.value)}>
            <MenuItem value={Sort.Number}>Number</MenuItem>
            <MenuItem value={Sort.Description}>Description</MenuItem>
            <MenuItem value={Sort.Easiest}>Easiest</MenuItem>
            <MenuItem value={Sort.Hardest}>Hardest</MenuItem>
          </Select>
        </FormControl>
      </Container>
      {content}
    </>
  );
}

export default Scorecard;